import {API_BASE} from "./config";
import {
    ActivityListResponse,
    ActivityQuery,
    AuditEventsResponse,
    HistoryListResponse,
    HistoryQuery,
    JwtPayload,
    OrderTimelineResponse,
    LoginResponse,
    Payment,
    PayinRequisites,
} from "../types";
import {parseJwt} from "../utils/jwt";
// Модуль-лист: там же, где живёт белый список типов чека, и обратных связей в
// сторону api у него нет.
import {withReceiptExtension} from "../components/payment-details/types";

export class ApiError extends Error {
    readonly status: number;
    readonly body: string;

    constructor(status: number, message: string, body = "") {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

type SessionRejectedListener = (token: string) => void;
const sessionRejectedListeners = new Set<SessionRejectedListener>();

/** Сервер не принял токен: вход закрыт (админ включил или перевыпустил код) — AuthProvider выводит на страницу входа. */
export function onSessionRejected(listener: SessionRejectedListener): () => void {
    sessionRejectedListeners.add(listener);
    return () => {
        sessionRejectedListeners.delete(listener);
    };
}

async function throwApiError(res: Response, token?: string | null): Promise<never> {
    const body = await res.text().catch(() => "");
    if (res.status === 401) {
        if (token) sessionRejectedListeners.forEach((listener) => listener(token));
        throw new ApiError(res.status, "Unauthorized", body);
    }
    if (res.status === 403) throw new ApiError(res.status, "Forbidden", body);
    if (res.status === 409) throw new ApiError(res.status, "Conflict", body);
    throw new ApiError(res.status, `API error: ${res.status}`, body);
}

export async function apiFetch<T = unknown>(
    path: string,
    token: string | null,
    options: RequestInit = {}
): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) return throwApiError(res, token);
    if (res.status === 204) return undefined as T;
    // Некоторые 200-ответы приходят с пустым телом (например POST
    // /notifications/{id}/read). res.json() на пустой строке падает — читаем
    // текст и парсим только непустой.
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

export async function apiFetchBlob(
    path: string,
    token: string | null,
    options: RequestInit = {}
): Promise<Blob> {
    if (!token) throw new ApiError(401, "Unauthorized");
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) return throwApiError(res, token);
    return res.blob();
}

/** Normalise API response — handles both `T[]` and `{ items: T[] }` */
export function toArray<T>(data: T[] | { items?: T[] }): T[] {
    return Array.isArray(data) ? data : data.items ?? [];
}

export function claimOrder(orderId: string, token: string | null): Promise<Payment> {
    return apiFetch<Payment>(`/orders/${orderId}/claim`, token, {method: "POST"});
}

/** Response of POST /payments/{id}/complete|cancel|appeal (backend OutPaymentResponse). */
export interface OutPaymentResponse {
    id: string;
    remoteId: string;
    bank: string;
    amount: number;
    clientCard: string | null;
    clientPhone: string | null;
    clientBank: string | null;
    clientFio: string;
    postback: string | null;
    sub1: string | null;
    sub2: string | null;
    sub3: string | null;
    amountReal: number | null;
    currencyRate: string | null;
    receiptUrl: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
}

/** Response of POST /payments/{id}/receipt. */
export interface ReceiptUploadResponse {
    receiptId: string;
    paymentId: string;
    url: string;
    createdAt: string;
}

export interface CompletePaymentPayload {
    fiatAmountReal: number;
    currencyRate: string;
    // receiptIds — чеки, которые трейдер предъявляет этим закрытием. Пусто —
    // сервер берёт единственный чек платежа, как было до мультичека. Порядок
    // сохраняется: он же станет порядком файлов в запросе на площадку.
    receiptIds?: string[];
}

/**
 * Реквизиты приёма. `orderAmount` — сумма заявки в копейках, которую трейдер
 * выбрал из диапазона мерчанта; у заявок без диапазона поле не отправляется.
 */
export type PayinRequisitesPayload = PayinRequisites & {orderAmount?: number};

// completePayment posts the actual amount + trader-set rate. The backend answers
// with OutPaymentResponse (not the my-payments item shape); for a completed
// payment its status is always terminal (success).
export function completePayment(paymentId: string, token: string | null, payload: CompletePaymentPayload): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${paymentId}/complete`, token, {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

export function cancelPayment(paymentId: string, token: string | null): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${paymentId}/cancel`, token, {method: "POST"});
}

// Подтверждение терминальной отмены заявки. requisiteLast4 уезжает пустой
// строкой, когда сверять нечего: у заявки нет сохранённого реквизита, и сервер
// в этом случае как раз требует пустоты.
export interface CancelOrderPayload {
    requisiteLast4: string;
    /** Полная сумма заявки в копейках. */
    fiatAmount: number;
}

// cancelOrderByTrader — соседняя ручка к cancelPayment и совсем другое действие:
// заявка закрывается насовсем, мерчанту уходит отказ, заявка кабинета
// отменяется на площадке. Отсюда и тело: сервер не примет её без ввода, который
// трейдер сделал руками.
export function cancelOrderByTrader(
    paymentId: string,
    token: string | null,
    payload: CancelOrderPayload
): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${paymentId}/cancel-order`, token, {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

// sendExchangePayout — «USDT отправлены» по заявке-обмену.
//
// Ни суммы, ни адреса в теле нет: они снимок заявки, и сервер их не принимает.
// Единственное поле — hash транзакции, и он же единственное доказательство
// перевода, который прошёл мимо нашей книги.
//
// Ответ — обновлённая карточка трейдера, а не результат платежа: платёж этой
// ручкой не меняется вовсе.
export function sendExchangePayout(
    paymentId: string,
    token: string | null,
    payload: ExchangePayoutSentPayload
): Promise<Payment> {
    return apiFetch<Payment>(`/payments/${paymentId}/exchange/payout-sent`, token, {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

export interface ExchangePayoutSentPayload {
    /** Hash транзакции TRON, ровно 64 hex-символа. */
    txHash: string;
}

export function appealPayment(paymentId: string, token: string | null): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${paymentId}/appeal`, token, {method: "POST"});
}

export function setPayinRequisites(paymentId: string, token: string | null, payload: PayinRequisitesPayload): Promise<OutPaymentResponse> {
    return apiFetch<OutPaymentResponse>(`/payments/${paymentId}/requisites`, token, {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

// uploadReceipt sends a multipart PDF to POST /payments/{id}/receipt. It cannot
// go through apiFetch, which forces Content-Type: application/json — for
// multipart the browser must set the boundary itself, so we omit Content-Type.
export async function uploadReceipt(paymentId: string, file: File, token: string | null): Promise<ReceiptUploadResponse> {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/payments/${paymentId}/receipt`, {
        method: "POST",
        headers: {Authorization: `Bearer ${token}`},
        body: form,
    });
    if (!res.ok) return throwApiError(res, token);
    return res.json() as Promise<ReceiptUploadResponse>;
}

// uploadAppealReceipt is the admin counterpart of uploadReceipt: it attaches a
// PDF to a payment in dispute via POST /payments/{id}/appeal-receipt. Same
// multipart handling (the browser sets the boundary, so no JSON Content-Type).
export async function uploadAppealReceipt(paymentId: string, file: File, token: string | null): Promise<ReceiptUploadResponse> {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/payments/${paymentId}/appeal-receipt`, {
        method: "POST",
        headers: {Authorization: `Bearer ${token}`},
        body: form,
    });
    if (!res.ok) return throwApiError(res, token);
    return res.json() as Promise<ReceiptUploadResponse>;
}

// Имя сохраняемого файла собирает вызывающий, и расширение он знать не может:
// чек бывает PDF, а бывает скриншотом. Последнее слово поэтому за типом
// ответа — иначе картинка ложилась бы на диск с расширением `.pdf` и не
// открывалась. Content-Disposition сервера для этого не годится: кросс-доменный
// ответ его в JS не отдаёт, а Content-Type отдаёт всегда.
export async function downloadReceipt(receiptUrl: string, token: string | null, fileName = "receipt.pdf"): Promise<void> {
    if (!token) throw new ApiError(401, "Unauthorized");

    const res = await fetch(receiptUrl, {
        headers: {Authorization: `Bearer ${token}`},
    });
    if (!res.ok) return throwApiError(res, token);

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = withReceiptExtension(fileName, blob.type);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

// downloadClientReceipt скачивает чек, приложенный КЛИЕНТОМ заявки-обмена.
//
// Адрес собирается здесь, из заявки и id чека, и больше нигде: сервер ссылку в
// карточке не отдаёт намеренно — второй способ добраться до чужого файла был бы
// вторым местом, где можно ошибиться с доступом.
export async function downloadClientReceipt(
    orderId: string,
    receiptId: string,
    token: string | null
): Promise<void> {
    await downloadReceipt(
        `${API_BASE}/orders/${orderId}/client-receipts/${receiptId}`,
        token,
        `client-receipt-${receiptId}.pdf`
    );
}

export async function getMyPayments(token: string | null): Promise<Payment[]> {
    const data = await apiFetch<Payment[] | { items?: Payment[] }>("/payments/my", token);
    return toArray(data);
}

/**
 * Response of GET /balance/team (backend team.BalanceResponse).
 *
 * `amount` is a ready-to-render human-readable decimal string: the backend
 * already applies currency scale with balance.FormatAtomicAmount. It MUST NOT
 * be parsed into `number` (float) for money math; render it as-is and do not
 * divide it by 10^scale.
 */
export interface TeamBalanceResponse {
    id: string;
    currency: string;
    amount: string;
    /**
     * Сколько из этого баланса можно запросить к выводу — сумма положительных
     * срезов по мерчантам. Отдельное от `amount` число, и оно бывает БОЛЬШЕ:
     * долг команды мерчанту баланс уменьшает, но выводом не гасится. Это же
     * число бэкенд использует как потолок запроса, поэтому подсказка в поле и
     * отказ ручки разойтись не могут.
     */
    withdrawable: string;
    lastBalanceResetDate: string;
}

export function getTeamBalance(token: string | null): Promise<TeamBalanceResponse> {
    return apiFetch<TeamBalanceResponse>("/balance/team", token);
}

export interface RapiraMerchantRate {
    merchantId: string;
    merchantName: string;
    markupPercent: string;
    rate: string | null;
}

export interface RapiraRateInfoResponse {
    enabled: boolean;
    baseRate: string | null;
    quotedAt: string | null;
    ageSeconds: number | null;
    /**
     * Котировка устарела настолько, что ей уже нельзя фиксировать курс новой
     * заявки. Решает бэкенд по своему конфигу — порог на клиенте не считаем и
     * не хардкодим, иначе он разъедется с сервером при первой же настройке.
     * Курс при этом продолжает приходить: видеть устаревшее число с пометкой
     * полезнее, чем пустой виджет.
     */
    stale: boolean;
    merchants: RapiraMerchantRate[];
}

export function getRapiraRateInfo(token: string | null): Promise<RapiraRateInfoResponse> {
    return apiFetch<RapiraRateInfoResponse>("/rate/rapira", token);
}

export interface BalanceMovementItem {
    id: string;
    delta: string;
    balanceAfter: string | null;
    reason: string;
    paymentId: string | null;
    actor: string;
    // occurredAt — когда событие случилось у источника, createdAt — когда строку
    // записали в книгу. Список приходит в порядке createdAt: balanceAfter это
    // нарастающий итог, снятый в момент записи.
    occurredAt: string;
    createdAt: string;
    merchantId?: string;
    merchantName?: string;
    teamId?: string;
    teamName?: string;
}

export interface BalanceMovementsResponse {
    items: BalanceMovementItem[];
    currency: string;
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

export interface BalanceMovementsParams {
    limit?: number;
    offset?: number;
}

function balanceMovementsQuery(query: BalanceMovementsParams = {}): string {
    const params = new URLSearchParams();
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    const suffix = params.toString();
    return suffix ? `?${suffix}` : "";
}

export function getTeamBalanceMovements(token: string | null, query: BalanceMovementsParams = {}): Promise<BalanceMovementsResponse> {
    return apiFetch<BalanceMovementsResponse>(`/balance/team/movements${balanceMovementsQuery(query)}`, token);
}

/**
 * Response of GET /balance/team/by-merchant (backend team.BalanceByMerchantResponse).
 *
 * `items[].amount` is mirrored to the merchant's side: negative means the
 * merchant owes the team. Zero slices are omitted by the backend. `unattributed`
 * is the (also mirrored) sum of ledger rows without a merchant — present only
 * when non-zero.
 */
export interface BalanceByMerchantItem {
    merchantId: string;
    merchantName: string;
    amount: string;
}

export interface BalanceByMerchantResponse {
    teamBalance: string;
    currency: string;
    items: BalanceByMerchantItem[];
    unattributed: string | null;
}

export function getTeamBalanceByMerchant(token: string | null): Promise<BalanceByMerchantResponse> {
    return apiFetch<BalanceByMerchantResponse>("/balance/team/by-merchant", token);
}

/**
 * Запрос вывода баланса команды (backend teamwithdrawal.WithdrawalRequestResponse).
 *
 * `amount` — замороженная в момент запроса сумма, `received` — сколько уже
 * пришло на привязанный адрес, `reset` — сколько фактически сброшено при
 * завершении (пусто, пока запрос не закрыт). Все три — готовые к отрисовке
 * десятичные строки USDT, как и остальные балансовые поля: парсить их в number
 * для денежной арифметики нельзя.
 */
export interface TeamWithdrawalSlice {
    merchantId: string | null;
    merchantName: string | null;
    amount: string;
    reset: string | null;
}

export type TeamWithdrawalStatus = "pending" | "completed" | "cancelled";

export interface TeamWithdrawalRequestResponse {
    id: string;
    status: TeamWithdrawalStatus;
    amount: string;
    received: string;
    reset: string | null;
    currency: string;
    network: string;
    address: string;
    createdAt: string;
    completedAt: string | null;
    completedBy: string | null;
    cancelledAt: string | null;
    cancelledBy: string | null;
    slices: TeamWithdrawalSlice[];
}

/** Кошелёк команды, на который трейдер может попросить вывод. */
export interface TeamPayoutWallet {
    id: string;
    network: string;
    address: string;
    activeFrom: string;
}

/** GET /balance/team/payout-addresses — кошельки своей команды, новый сверху. */
export function getTeamPayoutWallets(token: string | null): Promise<TeamPayoutWallet[]> {
    return apiFetch<{items: TeamPayoutWallet[]}>("/balance/team/payout-addresses", token)
        .then((resp) => resp.items ?? []);
}

/**
 * POST /balance/team/withdrawal-request — заморозить долг и запросить вывод.
 *
 * `amount` — десятичная строка USDT, как её ввёл трейдер; в number она не
 * переводится нигде по пути. Без суммы запрос уходит с пустым телом и означает
 * «весь доступный остаток» — ровно то, что кнопка отправляла до появления поля.
 * `addressId` — выбранный кошелёк команды; без него сервер берёт самый новый.
 */
export function createTeamWithdrawalRequest(
    token: string | null,
    amount?: string,
    addressId?: string,
): Promise<TeamWithdrawalRequestResponse> {
    const fields: {amount?: string; addressId?: string} = {};
    if (amount != null && amount.trim() !== "") fields.amount = amount.trim();
    if (addressId) fields.addressId = addressId;
    const body = Object.keys(fields).length > 0 ? JSON.stringify(fields) : undefined;
    return apiFetch<TeamWithdrawalRequestResponse>("/balance/team/withdrawal-request", token, {
        method: "POST",
        ...(body ? {body} : {}),
    });
}

/**
 * GET /balance/team/withdrawal-request — активный запрос, а если его нет —
 * последний завершённый или отменённый: итог только что закрытого вывода
 * трейдеру нужно увидеть.
 */
export function getTeamWithdrawalRequest(token: string | null): Promise<TeamWithdrawalRequestResponse> {
    return apiFetch<TeamWithdrawalRequestResponse>("/balance/team/withdrawal-request", token);
}

export function getActivity(token: string | null, query: ActivityQuery): Promise<ActivityListResponse> {
    const params = new URLSearchParams();
    if (query.scope) params.set("scope", query.scope);
    if (query.traderId) params.set("traderId", query.traderId);
    if (query.teamId) params.set("teamId", query.teamId);
    if (query.q) params.set("q", query.q);
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", query.sort);

    const suffix = params.toString();
    return apiFetch<ActivityListResponse>(`/activity${suffix ? `?${suffix}` : ""}`, token);
}

export function getAuditEvents(token: string | null, query: ActivityQuery): Promise<AuditEventsResponse> {
    const params = new URLSearchParams();
    if (query.scope) params.set("scope", query.scope);
    if (query.traderId) params.set("traderId", query.traderId);
    if (query.teamId) params.set("teamId", query.teamId);
    if (query.actorId) params.set("actorId", query.actorId);
    if (query.actorType) params.set("actorType", query.actorType);
    if (query.q) params.set("q", query.q);
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", query.sort);
    if (query.hideFailedLogins) params.set("hideFailedLogins", "true");

    const suffix = params.toString();
    return apiFetch<AuditEventsResponse>(`/audit-events${suffix ? `?${suffix}` : ""}`, token);
}

export function getOrderTimeline(token: string | null, orderId: string): Promise<OrderTimelineResponse> {
    return apiFetch<OrderTimelineResponse>(`/orders/${encodeURIComponent(orderId)}/timeline`, token);
}

export function getTraderHistory(token: string | null, query: HistoryQuery): Promise<HistoryListResponse> {
    const params = new URLSearchParams();
    if (query.scope) params.set("scope", query.scope);
    if (query.traderId) params.set("traderId", query.traderId);
    if (query.teamId) params.set("teamId", query.teamId);
    if (query.q) params.set("q", query.q);
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", query.sort);

    const suffix = params.toString();
    return apiFetch<HistoryListResponse>(`/payments/history${suffix ? `?${suffix}` : ""}`, token);
}

// Причина отказа приходит из Promise.catch, а туда бросают что угодно — и
// Error, и строку, и ответ сервера. Поэтому unknown, а не any: тип честно
// говорит «неизвестно что», и это единственное, что с ним делают — печатают.
const errorHandler = (name: string) => {
    return (t: unknown) => {
        console.log("Could not get result of", name, "request, reason:", t)
        return null
    }
}

export function signIn(login: string, password: string): Promise<JwtPayload | null> {
    return fetch(`${API_BASE}/auth/sign-in`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({login: login, password: password}),
    }).then(r => r.json()).then(res => {
        const data: LoginResponse = res;
        return parseJwt(data.jwt);
    }).catch(errorHandler("sign-in"))
}
